import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { EmptyState } from './EmptyState';
import { Spinner } from './Spinner';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  /** Column id currently sorted on. */
  columnId: string;
  /** Sort direction. */
  direction: SortDirection;
}

/**
 * Column definition for the generic `DataTable<T>` component.
 *
 * Use `accessor` to render a value derived from the row, or pass a `cell`
 * render function for arbitrary JSX. `sortable` enables the header click
 * affordance and the sort callback `onSort` propagation.
 */
export interface DataTableColumn<T> {
  /** Stable column identifier used for sorting and React keys. */
  id: string;
  /** Header content (string or arbitrary node). */
  header: ReactNode;
  /** Accessor returning a primitive value for the default cell. */
  accessor?: (row: T) => ReactNode;
  /** Custom cell renderer; takes precedence over `accessor`. */
  cell?: (row: T, rowIndex: number) => ReactNode;
  /** Marks the column as sortable; the parent owns sort state. */
  sortable?: boolean;
  /** Optional class for the `<th>`. */
  headerClassName?: string;
  /** Optional class for the `<td>`. */
  cellClassName?: string;
  /** Tailwind width utility (e.g. `w-32`). */
  width?: string;
  /** Text alignment helper. */
  align?: 'left' | 'center' | 'right';
}

export interface DataTableProps<T> {
  /** Column definitions. */
  columns: DataTableColumn<T>[];
  /** Rows to render. */
  data: T[];
  /** Stable key extractor (defaults to row index). */
  getRowId?: (row: T, index: number) => string | number;
  /** Optional row click handler. When set, rows get a hover/focus affordance. */
  onRowClick?: (row: T, index: number) => void;
  /** Optional sort state owned by the parent. */
  sort?: SortState;
  /** Sort change callback (only fired for sortable columns). */
  onSort?: (next: SortState | undefined) => void;
  /** Show a loading state instead of rows. */
  loading?: boolean;
  /** Custom empty state node; falls back to `<EmptyState>` defaults. */
  emptyState?: ReactNode;
  /** Optional pagination/footer node rendered below the table. */
  footer?: ReactNode;
  /** Extra classes for the table wrapper. */
  className?: string;
  /** Hide the sticky header (defaults to `false`). */
  stickyHeader?: boolean;
  /** Visually hidden caption for screen readers. */
  caption?: ReactNode;
}

const alignClass: Record<NonNullable<DataTableColumn<unknown>['align']>, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

function nextSortState(current: SortState | undefined, columnId: string): SortState | undefined {
  if (!current || current.columnId !== columnId) {
    return { columnId, direction: 'asc' };
  }
  if (current.direction === 'asc') {
    return { columnId, direction: 'desc' };
  }
  return undefined;
}

function getCellContent<T>(column: DataTableColumn<T>, row: T, rowIndex: number): ReactNode {
  if (column.cell) {
    return column.cell(row, rowIndex);
  }
  if (column.accessor) {
    return column.accessor(row);
  }
  return null;
}

function SortIcon({
  active,
  direction,
}: {
  active: boolean;
  direction: SortDirection | undefined;
}): ReactElement {
  if (!active) return <ChevronsUpDown size={14} className="ml-1 inline text-muted" aria-hidden />;
  return direction === 'asc' ? (
    <ChevronUp size={14} className="ml-1 inline text-primary" aria-hidden />
  ) : (
    <ChevronDown size={14} className="ml-1 inline text-primary" aria-hidden />
  );
}

/**
 * Generic data table primitive.
 *
 * Lightweight by design — sorting is parent-driven (no client-side sorting),
 * pagination is delegated via the `footer` slot, and styling is opinionated
 * (sticky header, alternating hover, dark theme).
 *
 * Usage:
 * ```tsx
 * <DataTable<Order>
 *   columns={[
 *     { id: 'id', header: 'ID', accessor: r => r.id, sortable: true },
 *     { id: 'status', header: 'Status', cell: r => <StatusBadge status={r.status} /> },
 *   ]}
 *   data={orders}
 *   sort={sort}
 *   onSort={setSort}
 *   onRowClick={(o) => navigate(`/orders/${o.id}`)}
 * />
 * ```
 */
export function DataTable<T>({
  columns,
  data,
  getRowId,
  onRowClick,
  sort,
  onSort,
  loading = false,
  emptyState,
  footer,
  className,
  stickyHeader = true,
  caption,
}: DataTableProps<T>): ReactElement {
  const handleSort = (column: DataTableColumn<T>): void => {
    if (!column.sortable || !onSort) return;
    onSort(nextSortState(sort, column.id));
  };

  const isInteractive = Boolean(onRowClick);

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div
        className={cn(
          'overflow-x-auto rounded-card border border-border bg-bg-card',
          stickyHeader && 'relative',
        )}
      >
        <table className="w-full border-collapse text-sm">
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          <thead
            className={cn(
              'bg-bg-card text-left text-xs uppercase tracking-wide text-muted',
              stickyHeader && 'sticky top-0 z-10',
            )}
          >
            <tr className="border-b border-border">
              {columns.map((col) => {
                const isActive = sort?.columnId === col.id;
                const ariaSort = isActive
                  ? sort?.direction === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : col.sortable
                    ? 'none'
                    : undefined;
                return (
                  <th
                    key={col.id}
                    scope="col"
                    aria-sort={ariaSort}
                    className={cn(
                      'px-4 py-3 font-semibold',
                      col.align && alignClass[col.align],
                      col.width,
                      col.sortable &&
                        'cursor-pointer select-none hover:text-white focus-visible:outline-none ' +
                          'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset',
                      col.headerClassName,
                    )}
                    onClick={col.sortable ? () => handleSort(col) : undefined}
                    tabIndex={col.sortable ? 0 : undefined}
                    onKeyDown={
                      col.sortable
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleSort(col);
                            }
                          }
                        : undefined
                    }
                  >
                    <span className="inline-flex items-center">
                      {col.header}
                      {col.sortable ? (
                        <SortIcon active={isActive} direction={isActive ? sort?.direction : undefined} />
                      ) : null}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12">
                  <div className="flex items-center justify-center gap-2 text-muted">
                    <Spinner size="sm" />
                    <span className="text-sm">Carregando…</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="p-4">
                  {emptyState ?? <EmptyState title="Nenhum registro" />}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const rowKey = getRowId ? getRowId(row, rowIndex) : rowIndex;
                return (
                  <tr
                    key={rowKey}
                    className={cn(
                      'border-b border-border-subtle transition-colors last:border-b-0',
                      isInteractive &&
                        'cursor-pointer hover:bg-bg-hover/60 focus-within:bg-bg-hover/60',
                    )}
                    onClick={isInteractive ? () => onRowClick?.(row, rowIndex) : undefined}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        className={cn(
                          'px-4 py-3 text-white',
                          col.align && alignClass[col.align],
                          col.cellClassName,
                        )}
                      >
                        {getCellContent(col, row, rowIndex)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {footer ? <div>{footer}</div> : null}
    </div>
  );
}
